import webStorageKeys from '@/constants/web-storage-keys';

class GoldenDuelStorageService {
	private static instance: GoldenDuelStorageService;
	private readonly STORAGE_KEY = webStorageKeys.GOLDEN_DUAL_TRANS;
	private readonly TOOLTIP_KEY = webStorageKeys.HAS_SHOWN_GOLDEN_DUAL_TOOLTIP;
	private readonly DRAW_TIME_KEY = webStorageKeys.GOLDEN_DUAL_DRAW_TIME;

	private constructor() {}

	public static getInstance(): GoldenDuelStorageService {
		if (!GoldenDuelStorageService.instance) {
			GoldenDuelStorageService.instance = new GoldenDuelStorageService();
		}
		return GoldenDuelStorageService.instance;
	}

	/**
   * Get all stored transaction IDs
   * @returns Array of transaction IDs
   */
	public getTransactionIds(): string[] {
		const storedTransIds = localStorage.getItem(this.STORAGE_KEY);
		return storedTransIds ? storedTransIds.split(',') : [];
	}

	/**
   * Get unique transaction IDs count
   * @returns Number of unique transaction IDs
   */
	public getUniqueTransactionCount(): number {
		const transIds = this.getTransactionIds();
		return new Set(transIds).size;
	}

	/**
   * Add a new transaction ID
   * @param transId Transaction ID to add
   */
	public addTransactionId(transId: string): void {
		if (!transId) return;

		const currentIds = this.getTransactionIds();
		if (currentIds.includes(transId)) return;

		const updatedIds = [...currentIds, transId];
		localStorage.setItem(this.STORAGE_KEY, updatedIds.join(','));
	}

	/**
   * Clear all stored transaction IDs
   */
	public clearTransactionIds(): void {
		localStorage.removeItem(this.STORAGE_KEY);
	}

	/**
   * Check if there are any stored transaction IDs
   * @returns boolean indicating if there are stored IDs
   */
	public hasStoredTransactions(): boolean {
		return this.getTransactionIds().length > 0;
	}

	/**
   * Check if the tooltip has been shown
   * @returns boolean indicating if tooltip has been shown
   */
	public hasShownTooltip(): boolean {
		return localStorage.getItem(this.TOOLTIP_KEY) === 'true';
	}

	/**
   * Mark the tooltip as shown
   */
	public markTooltipAsShown(): void {
		localStorage.setItem(this.TOOLTIP_KEY, 'true');
	}

	/**
   * Check if should show the tooltip
   * @returns boolean indicating if tooltip should be shown
   */
	public shouldShowTooltip(): boolean {
		return !this.hasShownTooltip() && this.hasStoredTransactions();
	}

	/**
   * Store draw time in localStorage
   * @param drawTime ISO string of draw time
   */
	public setDrawTime(drawTime: string): void {
		if (!drawTime) return;
		
		const currentDrawTime = this.getDrawTime();
		
		// If no current draw time exists or it's invalid, set the new one
		if (!currentDrawTime || isNaN(new Date(currentDrawTime).getTime())) {
			localStorage.setItem(this.DRAW_TIME_KEY, drawTime);
			return;
		}
		
		// Only update if new draw time is earlier than the existing one
		if (new Date(drawTime) < new Date(currentDrawTime)) {
			localStorage.setItem(this.DRAW_TIME_KEY, drawTime);
		}
	}

	/**
   * Get stored draw time
   * @returns Draw time as ISO string or null if not set
   */
	public getDrawTime(): string | null {
		return localStorage.getItem(this.DRAW_TIME_KEY);
	}

	/**
   * Clear stored draw time
   */
	public clearDrawTime(): void {
		localStorage.removeItem(this.DRAW_TIME_KEY);
	}
}

export const goldenDuelStorageService = GoldenDuelStorageService.getInstance(); 